/**
 * User tracking service for analytics
 * Tracks page views, screen time, events, and user behavior
 */

// Strip whitespace, real newlines, AND literal "\n"/"\r" sequences from env
// values pasted with stray escapes (see services/api.ts for the full story).
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1')
  .replace(/(\\[rn])+$/g, '')
  .replace(/[\r\n\t\s]+$/g, '')
  .replace(/^\s+/g, '');

// Generate or retrieve session ID
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('tracking_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('tracking_session_id', sessionId);
  }
  return sessionId;
}

// Get auth token
function getAuthToken(): string | null {
  return localStorage.getItem('access_token');
}

// Check if tracking is enabled (cookie consent)
function isTrackingEnabled(): boolean {
  const consent = localStorage.getItem('cookie_consent');
  if (!consent) return false;
  try {
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

// Track page view
export async function trackPageView(
  pagePath: string,
  pageTitle?: string,
  referrer?: string
): Promise<void> {
  if (!isTrackingEnabled()) return;

  try {
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    
    // Calculate view duration (if available from previous page)
    const viewStart = sessionStorage.getItem(`view_start_${pagePath}`);
    let viewDuration: number | undefined;
    if (viewStart) {
      viewDuration = Date.now() - parseInt(viewStart);
    }
    
    // Store start time for this page
    sessionStorage.setItem(`view_start_${pagePath}`, Date.now().toString());

    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(`${API_BASE_URL}/tracking/page-view`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        page_path: pagePath,
        page_title: pageTitle || document.title,
        referrer: referrer || document.referrer,
        view_duration: viewDuration,
        screen_width: screenWidth,
        screen_height: screenHeight,
      }),
    });
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
}

// Track screen time
let screenTimeInterval: NodeJS.Timeout | null = null;
let lastActiveTime = Date.now();
let totalTimeSpent = 0;
let currentPagePath = '';

export function startScreenTimeTracking(pagePath: string): void {
  if (!isTrackingEnabled()) return;

  // Stop previous tracking if any
  stopScreenTimeTracking();

  currentPagePath = pagePath;
  totalTimeSpent = 0;
  lastActiveTime = Date.now();

  // Track active time every 10 seconds
  screenTimeInterval = setInterval(() => {
    const now = Date.now();
    const timeDiff = now - lastActiveTime;
    
    // Only count if user was active (less than 30 seconds since last activity)
    if (timeDiff < 30000) {
      totalTimeSpent += timeDiff;
    }
    
    lastActiveTime = now;

    // Send update every 30 seconds
    if (totalTimeSpent >= 30000) {
      sendScreenTimeUpdate();
      totalTimeSpent = 0;
    }
  }, 10000);

  // Track visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Track user activity
  ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
    document.addEventListener(event, updateLastActiveTime, { passive: true });
  });
}

export function stopScreenTimeTracking(): void {
  if (screenTimeInterval) {
    clearInterval(screenTimeInterval);
    screenTimeInterval = null;
  }
  
  // Send final update
  sendScreenTimeUpdate();
  
  // Remove event listeners
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
    document.removeEventListener(event, updateLastActiveTime);
  });
}

function updateLastActiveTime(): void {
  lastActiveTime = Date.now();
}

function handleVisibilityChange(): void {
  if (document.hidden) {
    // Page is hidden, send current time
    sendScreenTimeUpdate();
  } else {
    // Page is visible again, reset last active time
    lastActiveTime = Date.now();
  }
}

function handleBeforeUnload(): void {
  sendScreenTimeUpdate();
}

async function sendScreenTimeUpdate(): Promise<void> {
  if (!currentPagePath || totalTimeSpent === 0) return;

  try {
    const scrollDepth = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    );

    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(`${API_BASE_URL}/tracking/screen-time`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        session_id: getSessionId(),
        page_path: currentPagePath,
        time_spent: Math.round(totalTimeSpent / 1000), // Convert to seconds
        is_active: !document.hidden,
        scroll_depth: scrollDepth,
      }),
    });

    totalTimeSpent = 0;
  } catch (error) {
    console.error('Failed to track screen time:', error);
  }
}

// Track user events (clicks, etc.)
export async function trackEvent(
  eventType: string,
  eventName: string,
  eventValue?: string,
  elementId?: string,
  elementClass?: string,
  elementText?: string
): Promise<void> {
  if (!isTrackingEnabled()) return;

  try {
    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/tracking/event`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        session_id: getSessionId(),
        event_type: eventType,
        event_name: eventName,
        event_value: eventValue,
        page_path: window.location.pathname,
        element_id: elementId,
        element_class: elementClass,
        element_text: elementText,
      }),
    });
    // Silently ignore 404 errors (tracking endpoint may not exist)
    if (!response.ok && response.status !== 404) {
      throw new Error(`Tracking failed with status ${response.status}`);
    }
  } catch (error) {
    // Only log non-404 errors
    if (error instanceof Error && !error.message.includes('404')) {
      console.error('Failed to track event:', error);
    }
  }
}

// Auto-track clicks on buttons and links
export function enableClickTracking(): void {
  if (!isTrackingEnabled()) return;

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target) return;

    const elementId = target.id || undefined;
    const elementClass = target.className || undefined;
    const elementText = target.textContent?.trim().substring(0, 100) || undefined;

    // Track button clicks
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      trackEvent('click', 'button_click', undefined, elementId, elementClass, elementText);
    }
    
    // Track link clicks
    if (target.tagName === 'A' || target.closest('a')) {
      const href = (target as HTMLAnchorElement).href || target.closest('a')?.getAttribute('href');
      trackEvent('click', 'link_click', href || undefined, elementId, elementClass, elementText);
    }
  }, true);
}





